import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { serieCrescita } from '@mastro/core/previsionale';
import { api, type Categoria } from '../api';
import {
  Campo,
  CampoImporto,
  Caricamento,
  Dialogo,
  Errore,
  Importo,
  Scheda,
  Vuoto,
  useAlberoCategorie,
  useAvvisi,
  useCarico,
} from '../componenti/base';
import { formattaEuro, meseDi, oggi } from '../formato';
import { useSessione } from '../main';


function mesiDa(inizio: string, quanti: number): string[] {
  const [a, m] = inizio.split('-').map(Number);
  return Array.from({ length: quanti }, (_, i) => {
    const d = new Date(Date.UTC(a, m - 1 + i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

/**
 * La pagina delle voci ricorrenti.
 *
 * C'era una griglia categorie × mesi con una casella da riempire per ognuna.
 * È stata tolta il 27 ago 2026: nessuno l'aveva mai usata — **zero righe in un
 * anno di esercizio** — e la ragione si legge nelle voci qui sotto. Un numero
 * posato su una casella non dice cosa sia; «Incassi ISP, 22.000 € al mese da
 * settembre, +3 %» si legge, si discute e si corregge. Le voci fanno la stessa
 * cosa dicendo anche perché.
 */
export function Budget() {
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);

  if (categorie.caricamento) return <Caricamento />;
  if (categorie.errore)
    return (
      <div className="p-6">
        <Errore testo={categorie.errore} riprova={categorie.ricarica} />
      </div>
    );

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Budget</h1>
        <p className="max-w-3xl text-sm text-inchiostro-500">
          Quello che si prevede <em>oltre</em> i documenti già emessi: nel previsionale vale il maggiore fra i due, mai
          la somma. Si scrive come voci che si ripetono da sole — con la crescita attesa, se ci si aspetta di crescere.
        </p>
      </header>

      <Ricorrenti categorie={categorie.dati ?? []} onCambiate={categorie.ricarica} />
    </div>
  );
}

interface Versione {
  id: string;
  validita: string;
  importoCent: number;
  /** `numeric` di Postgres: arriva come stringa. */
  crescitaPercentuale: string;
  nota: string | null;
}

interface Ricorrente {
  id: string;
  nome: string;
  categoriaId: string;
  categoria: { id: string; nome: string };
  verso: 'ENTRATA' | 'USCITA';
  passoMesi: number;
  primoMese: string;
  ultimoMese: string | null;
  giornoDelMese: number;
  natura: Natura;
  attiva: boolean;
  versioni: Versione[];
}

type Natura = 'SCADENZA' | 'STIMA';

/**
 * Le due nature di una voce, dette come le dice chi le scrive.
 *
 * La differenza pesa nel mese in corso: una scadenza deve ancora arrivare e
 * resta nel previsto, una stima no — quel mese ha già i suoi incassi e le sue
 * fatture, e sommarci la stima contava i ricavi due volte.
 */
const NATURE: Record<Natura, { nome: string; spiegazione: string }> = {
  SCADENZA: {
    nome: 'Scadenza fissa',
    spiegazione: 'Un pagamento o un incasso che arriverà: stipendi, rate, affitto. Pesa anche sul mese in corso.',
  },
  STIMA: {
    nome: 'Stima',
    spiegazione:
      'Quanto ci si aspetta, non una scadenza: ricavi o costi attesi. Pesa dai mesi futuri; nel mese in corso contano i fatti.',
  },
};

const PASSI: { mesi: number; nome: string }[] = [
  { mesi: 1, nome: 'ogni mese' },
  { mesi: 2, nome: 'ogni 2 mesi' },
  { mesi: 3, nome: 'ogni trimestre' },
  { mesi: 6, nome: 'ogni semestre' },
  { mesi: 12, nome: 'una volta l’anno' },
];

/** La versione con `validita` aperta è quella in vigore adesso. */
function versioneInVigore(r: Ricorrente): Versione | undefined {
  return r.versioni.find((v) => v.validita.endsWith(',)')) ?? r.versioni.at(-1);
}

function importoInVigore(r: Ricorrente): number {
  return versioneInVigore(r)?.importoCent ?? 0;
}

function crescitaInVigore(r: Ricorrente): number {
  return Number(versioneInVigore(r)?.crescitaPercentuale ?? 0);
}

/**
 * Come si legge una percentuale di crescita: `+3 %` o `−1,5 %`, con il segno
 * davanti perché il verso conta più della cifra.
 */
/**
 * Il giorno del mese di una voce, modificabile sul posto.
 *
 * Prima si sceglieva solo creando la voce, e il modulo proponeva l'1: su
 * Erretielle «Costi ISP» usciva così il primo del mese, e il previsionale
 * trovava un buco di cassa a inizio ottobre che forse non esiste. Il giorno
 * decide dove cade il punto più basso: deve potersi correggere con un clic.
 */
function GiornoModificabile({ giorno, onSalva }: { giorno: number; onSalva: (g: number) => Promise<void> }) {
  const [aperto, setAperto] = useState(false);
  const [valore, setValore] = useState(String(giorno));
  const [salvo, setSalvo] = useState(false);

  if (!aperto) {
    return (
      <button
        className="num rounded px-1.5 py-0.5 underline decoration-dotted underline-offset-4 hover:bg-inchiostro-100"
        title="Cambia il giorno del mese"
        onClick={() => {
          setValore(String(giorno));
          setAperto(true);
        }}
      >
        {giorno}
      </button>
    );
  }

  const n = Number(valore);
  const valido = Number.isInteger(n) && n >= 1 && n <= 31;
  const conferma = async () => {
    if (!valido) return;
    if (n === giorno) return setAperto(false);
    setSalvo(true);
    await onSalva(n);
    setSalvo(false);
    setAperto(false);
  };

  return (
    <span className="inline-flex items-center gap-1">
      <input
        className={`campo num w-14 py-0.5 text-right ${valido ? '' : 'border-allerta'}`}
        inputMode="numeric"
        autoFocus
        value={valore}
        disabled={salvo}
        onChange={(e) => setValore(e.target.value.replace(/\D/g, '').slice(0, 2))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void conferma();
          if (e.key === 'Escape') setAperto(false);
        }}
        onBlur={() => void conferma()}
        title="Da 1 a 31. Nei mesi più corti si usa l'ultimo giorno."
      />
    </span>
  );
}

interface PropostaRicorrente {
  anagraficaId: string;
  nome: string;
  verso: 'ENTRATA' | 'USCITA';
  frequenza: 'MENSILE' | 'BIMESTRALE' | 'TRIMESTRALE' | 'IRREGOLARE';
  passoMesi: number;
  confidenza: 'CERTO' | 'PROBABILE' | 'DA_VERIFICARE';
  occorrenze: number;
  mesiFinestra: number;
  importoCent: number;
  mediaCent: number;
  minimoCent: number;
  massimoCent: number;
  ultimiImportiCent: number[];
  variabilita: number;
  prossimoMese: string;
  giorno: number;
  giornoDa: 'PAGAMENTI' | 'SCADENZE';
  categoriaId: string | null;
  categoria: string | null;
  sovrapposte: { nome: string; importoCent: number }[];
  /** Il nome della voce che già la rappresenta, se c'è. */
  giaNelBudget: string | null;
}

const NOMI_FREQUENZA: Record<PropostaRicorrente['frequenza'], string> = {
  MENSILE: 'mensile',
  BIMESTRALE: 'ogni 2 mesi',
  TRIMESTRALE: 'trimestrale',
  IRREGOLARE: 'irregolare',
};

const CONFIDENZE: Record<PropostaRicorrente['confidenza'], { nome: string; classe: string; spiegazione: string }> = {
  CERTO: {
    nome: 'ricorrente certo',
    classe: 'text-incassi',
    spiegazione: 'Torna quasi ogni mese con importi simili: ha l’andamento di un canone.',
  },
  PROBABILE: {
    nome: 'ricorrente probabile',
    classe: 'text-saldo',
    spiegazione: 'Il ritmo c’è, ma i punti sono pochi (un trimestrale in sei mesi si vede due volte) o gli importi variano.',
  },
  DA_VERIFICARE: {
    nome: 'da verificare',
    classe: 'text-attenzione',
    spiegazione: 'Fattura spesso ma senza un ritmo, o con importi troppo diversi per stimarne uno solo.',
  },
};

/**
 * La casella «seleziona tutti» di un'intestazione, con i suoi tre stati: vuota,
 * piena, e a metà quando ne sono scelte solo alcune — che nel DOM non è un
 * attributo ma una proprietà, quindi si imposta da un riferimento. Accanto,
 * quante sono scelte: la tabella scorre, e le spunte in fondo non si vedono.
 */
function SelezionaTutti({
  scelte,
  totale,
  onCambia,
}: {
  scelte: number;
  totale: number;
  onCambia: (tutte: boolean) => void;
}) {
  const casella = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (casella.current) casella.current.indeterminate = scelte > 0 && scelte < totale;
  }, [scelte, totale]);
  if (totale === 0) return null;
  return (
    <label className="inline-flex items-center gap-1.5 font-normal normal-case" title="Seleziona tutte le righe di questa tabella">
      <input
        ref={casella}
        type="checkbox"
        checked={scelte === totale}
        onChange={() => onCambia(scelte < totale)}
        aria-label="Seleziona tutti"
      />
      <span className="num text-[10px] text-inchiostro-500">
        {scelte} di {totale}
      </span>
    </label>
  );
}

/** «2026-12» → «dic 2026». */
function nomeMese(mese: string): string {
  const [a, m] = mese.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, 1)).toLocaleDateString('it-IT', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Le ricorrenze rilevate dallo storico: fornitori e clienti che tornano con un
 * ritmo, quanto e quando torneranno.
 *
 * Nei mesi futuri il previsionale conosce solo le fatture già arrivate: i costi
 * che tornano — la connettività ogni mese, il commercialista ogni trimestre —
 * mancano finché la fattura non c'è, e la cassa di oggi sembra più libera di
 * quanto sia. Qui lo storico degli ultimi sei mesi chiusi diventa previsione.
 *
 * Suggerisce soltanto. Niente è spuntato e niente entra nel budget da solo:
 * «Aggiungi al budget» apre il modulo già compilato, da correggere prima di
 * salvare — frequenza, importo, mese di partenza, durata. Ogni voce creata è
 * una stima legata alla sua controparte: quando la fattura vera arriva, prende
 * il posto della stima di quel mese invece di sommarsi.
 */
function ProposteRicorrenti({ categorie, onCreate }: { categorie: Categoria[]; onCreate: () => void }) {
  const avvisa = useAvvisi();
  const { utente, organizzazione } = useSessione();
  const dati = useCarico(
    () =>
      api.get<{
        mesi: string[];
        proposte: PropostaRicorrente[];
        escluse: { anagraficaId: string; nome: string }[];
        disattivati?: boolean;
      }>('/api/ricorrenti/proposte'),
    [],
  );
  const [aperto, setAperto] = useState<'USCITA' | 'ENTRATA' | null>(null);
  const [selezionate, setSelezionate] = useState<Set<string>>(new Set());
  const [creo, setCreo] = useState(false);
  const [daAggiungere, setDaAggiungere] = useState<PropostaRicorrente | null>(null);
  const [vediEscluse, setVediEscluse] = useState(false);

  const proposte = dati.dati?.proposte ?? [];
  const escluse = dati.dati?.escluse ?? [];
  if (dati.caricamento || (proposte.length === 0 && escluse.length === 0)) return null;

  /**
   * Toglie una controparte dai suggerimenti, o la rimette. Resta salvato
   * sull'anagrafica: i suggerimenti si ricalcolano a ogni apertura, e senza
   * questo chi non si vuole più considerare ricompariva ogni volta.
   */
  const escludi = async (anagraficaId: string, nome: string, escludere: boolean) => {
    try {
      await api.put(`/api/anagrafiche/${anagraficaId}/suggerimenti`, { escludi: escludere });
      avvisa(escludere ? `«${nome}» non verrà più suggerito.` : `«${nome}» torna fra i suggerimenti.`);
      dati.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Non riuscito.', 'errore');
    }
  };
  const mesi = dati.dati!.mesi;

  const chiave = (p: PropostaRicorrente) => `${p.anagraficaId}|${p.verso}`;
  const alterna = (p: PropostaRicorrente) => {
    const n = new Set(selezionate);
    n.has(chiave(p)) ? n.delete(chiave(p)) : n.add(chiave(p));
    setSelezionate(n);
  };

  const spegni = async () => {
    if (!confirm('Non suggerire più voci dai fornitori e clienti che si ripetono?\n\nSi riattivano da Impostazioni.')) return;
    try {
      await api.put('/api/organizzazione/suggerimenti-ricorrenti', { attivi: false });
      avvisa('Suggerimenti disattivati. Si riattivano da Impostazioni → Tesoreria.');
      dati.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Non riuscito.', 'errore');
    }
  };

  /** Le scelte, così come sono proposte: ognuna dal suo prossimo mese, col suo ritmo. */
  const creaScelte = async (verso: 'USCITA' | 'ENTRATA') => {
    const daCreare = proposte.filter(
      (p) => p.verso === verso && selezionate.has(chiave(p)) && p.categoriaId && !p.giaNelBudget,
    );
    if (daCreare.length === 0) return;
    setCreo(true);
    let create = 0;
    try {
      for (const p of daCreare) {
        await api.post('/api/ricorrenti', {
          nome: p.nome,
          categoriaId: p.categoriaId,
          verso: p.verso,
          passoMesi: p.passoMesi,
          primoMese: p.prossimoMese,
          giornoDelMese: p.giorno,
          natura: 'STIMA',
          importoCent: p.importoCent,
          crescitaPercentuale: 0,
          anagraficaId: p.anagraficaId,
        });
        create += 1;
      }
      avvisa(
        `${create} ${create === 1 ? 'voce aggiunta' : 'voci aggiunte'} al budget. ` +
          'Se una stima già scritta le comprendeva, ricordati di ridurla o chiuderla.',
      );
    } catch (e) {
      avvisa(`${create} aggiunte, poi: ${e instanceof Error ? e.message : 'errore'}`, 'errore');
    } finally {
      setCreo(false);
      setSelezionate(new Set());
      dati.ricarica();
      onCreate();
    }
  };

  const gruppo = (verso: 'USCITA' | 'ENTRATA') => {
    const elenco = proposte.filter((p) => p.verso === verso);
    if (elenco.length === 0) return null;
    const gia = elenco.filter((p) => p.giaNelBudget);
    const daValutare = elenco.filter((p) => !p.giaNelBudget);
    const conta = (c: PropostaRicorrente['confidenza']) => daValutare.filter((p) => p.confidenza === c).length;
    const scelti = daValutare.filter((p) => selezionate.has(chiave(p)));
    const selezionabili = daValutare.filter((p) => p.categoriaId);
    const totaleScelti = scelti.reduce((t, p) => t + p.importoCent / p.passoMesi, 0);
    const stime = new Map<string, number>();
    for (const p of daValutare) for (const x of p.sovrapposte) stime.set(x.nome, x.importoCent);
    const titolo = verso === 'USCITA' ? 'Costi ricorrenti rilevati' : 'Incassi ricorrenti rilevati';
    const chi = verso === 'USCITA' ? 'fornitori' : 'clienti';

    return (
      <div className="border-t border-inchiostro-200 first:border-t-0">
        <button
          className="flex w-full flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-left hover:bg-inchiostro-50"
          onClick={() => setAperto(aperto === verso ? null : verso)}
        >
          <span className="font-medium">
            {aperto === verso ? '−' : '+'} {titolo}
          </span>
          <span className="text-sm text-inchiostro-500">
            {elenco.length} {chi} con un ritmo · {gia.length} già nel budget · {daValutare.length} da valutare (
            {conta('CERTO')} certi, {conta('PROBABILE')} probabili, {conta('DA_VERIFICARE')} da verificare)
          </span>
        </button>
        {aperto === verso && (
          <div className="px-4 pb-4">
            {stime.size > 0 && (
              <p className="mb-3 rounded-md bg-attenzione-chiaro px-3 py-2 text-sm text-attenzione">
                Stanno sulla stessa voce di{' '}
                {[...stime.entries()].map(([nome, cent], i) => (
                  <span key={nome}>
                    {i > 0 && ', '}«{nome}» (<span className="num">{formattaEuro(cent)}</span> al mese)
                  </span>
                ))}
                . Se quella stima li comprende già, aggiungendoli conteresti due volte gli stessi soldi: riducila di
                quanto aggiungi, o chiudila.
              </p>
            )}
            <div className="max-h-[32rem] overflow-y-auto">
              <table className="dati">
                <thead>
                  <tr>
                    <th className="whitespace-nowrap">
                      {/* Con 138 clienti spuntarli uno per uno non si fa. La
                          casella sceglie le righe di questa tabella che si
                          possono scegliere — non quelle già nel budget né
                          quelle senza una voce — e non tocca l'altra tabella. */}
                      <SelezionaTutti
                        scelte={selezionabili.filter((p) => selezionate.has(chiave(p))).length}
                        totale={selezionabili.length}
                        onCambia={(tutte) => {
                          const n = new Set(selezionate);
                          for (const p of selezionabili) {
                            if (tutte) n.add(chiave(p));
                            else n.delete(chiave(p));
                          }
                          setSelezionate(n);
                        }}
                      />
                    </th>
                    <th>{verso === 'USCITA' ? 'Fornitore' : 'Cliente'}</th>
                    <th>Frequenza</th>
                    <th className="text-right">Importo</th>
                    <th>Ultime volte</th>
                    <th>Prossima {verso === 'USCITA' ? 'uscita' : 'entrata'}</th>
                    <th>Confidenza</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {elenco.map((p) => {
                    const conf = CONFIDENZE[p.confidenza];
                    return (
                      <tr key={chiave(p)} className={p.giaNelBudget ? 'text-inchiostro-400' : ''}>
                        <td>
                          {!p.giaNelBudget && (
                            <input
                              type="checkbox"
                              checked={selezionate.has(chiave(p))}
                              disabled={!p.categoriaId}
                              onChange={() => alterna(p)}
                              aria-label={`Scegli ${p.nome}`}
                            />
                          )}
                        </td>
                        <td className="max-w-56 truncate" title={`${p.nome}${p.categoria ? ` · ${p.categoria}` : ''}`}>
                          {p.nome}
                        </td>
                        <td className="whitespace-nowrap text-xs">
                          {NOMI_FREQUENZA[p.frequenza]}
                          <span className="block text-inchiostro-400">
                            {p.occorrenze} volte su {p.mesiFinestra} mesi
                          </span>
                        </td>
                        <td
                          className="whitespace-nowrap text-right"
                          title={`Mediana ${formattaEuro(p.importoCent)}, media ${formattaEuro(p.mediaCent)}: la mediana non si fa tirare da una fattura anomala.`}
                        >
                          <Importo cent={p.importoCent} tono={verso === 'ENTRATA' ? 'incassi' : 'uscite'} />
                          <span className="block text-[10px] text-inchiostro-400">media {formattaEuro(p.mediaCent)}</span>
                        </td>
                        <td className="max-w-64 text-xs text-inchiostro-500">
                          {p.ultimiImportiCent.map((c) => formattaEuro(c).replace(/\s?€/, '')).join(' / ')}
                        </td>
                        <td className="whitespace-nowrap text-xs">
                          {nomeMese(p.prossimoMese)}
                          <span
                            className="block text-inchiostro-400"
                            title={
                              p.giornoDa === 'PAGAMENTI'
                                ? 'Il giorno in cui i pagamenti passano davvero in banca'
                                : 'Dalle scadenze: dei pagamenti non si sa abbastanza'
                            }
                          >
                            il {p.giorno}
                            {p.giornoDa === 'SCADENZE' ? ' (scad.)' : ''}
                          </span>
                        </td>
                        <td className={`whitespace-nowrap text-xs ${conf.classe}`} title={conf.spiegazione}>
                          {conf.nome}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          <button
                            className="mr-1.5 px-1 text-xs text-inchiostro-400 underline decoration-dotted underline-offset-2 hover:text-inchiostro-800"
                            title="Non suggerirlo più. Si rimette dall'elenco degli esclusi, in fondo."
                            onClick={() => void escludi(p.anagraficaId, p.nome, true)}
                          >
                            Escludi
                          </button>
                          {p.giaNelBudget ? (
                            <span className="text-xs" title="Una voce ricorrente già legata a questa controparte">
                              già nel budget
                            </span>
                          ) : (
                            <button
                              className="bottone-secondario px-2 py-1 text-xs"
                              disabled={!p.categoriaId}
                              title={p.categoriaId ? 'Apre la voce già compilata: la correggi prima di salvare' : 'Serve una voce del previsionale per questa controparte'}
                              onClick={() => setDaAggiungere(p)}
                            >
                              Aggiungi al budget…
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
              {scelti.length > 0 && (
                <span className="text-sm text-inchiostro-500">
                  Le scelte valgono circa <span className="num">{formattaEuro(Math.round(totaleScelti))}</span> al mese
                </span>
              )}
              <button className="bottone-primario" disabled={creo || scelti.length === 0} onClick={() => void creaScelte(verso)}>
                {creo
                  ? 'Aggiungo…'
                  : `Aggiungi ${scelti.length} ${scelti.length === 1 ? 'scelta' : 'scelte'} così come sono`}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Scheda
        titolo="Ricorrenze rilevate dallo storico"
        sottotitolo={`Analizzati ${nomeMese(mesi[0])} – ${nomeMese(mesi.at(-1)!)}: fornitori e clienti che tornano con un ritmo — mensile, ogni due mesi, trimestrale — con l’importo mediano e quando torneranno. Sono suggerimenti: niente è scelto, e ogni voce si può correggere prima di aggiungerla. Una voce aggiunta è una stima legata alla sua controparte: quando arriva la fattura vera prende il posto della stima di quel mese, invece di sommarsi.`}
        senzaPadding
        azioni={
          utente.ruolo === 'ADMIN' ? (
            <button className="bottone-secondario px-2 py-1 text-xs" onClick={() => void spegni()}>
              Non suggerire più
            </button>
          ) : undefined
        }
      >
        {gruppo('USCITA')}
        {gruppo('ENTRATA')}
        {escluse.length > 0 && (
          <div className="border-t border-inchiostro-200 px-4 py-3 text-sm">
            <button
              className="text-inchiostro-500 underline decoration-dotted underline-offset-2"
              onClick={() => setVediEscluse(!vediEscluse)}
            >
              {escluse.length === 1 ? '1 controparte esclusa' : `${escluse.length} controparti escluse`} dai suggerimenti
            </button>
            {vediEscluse && (
              <ul className="mt-2 space-y-1">
                {escluse.map((e) => (
                  <li key={e.anagraficaId} className="flex items-center justify-between gap-3">
                    <span>{e.nome}</span>
                    <button
                      className="bottone-secondario px-2 py-0.5 text-xs"
                      onClick={() => void escludi(e.anagraficaId, e.nome, false)}
                    >
                      Rimetti
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Scheda>

      {daAggiungere && (
        <FormRicorrente
          categorie={categorie}
          formatoData={organizzazione.formatoData}
          iniziale={{
            nome: daAggiungere.nome,
            verso: daAggiungere.verso,
            categoriaId: daAggiungere.categoriaId,
            importoCent: daAggiungere.importoCent,
            passoMesi: daAggiungere.passoMesi,
            primoMese: daAggiungere.prossimoMese,
            giornoDelMese: daAggiungere.giorno,
            natura: 'STIMA',
            anagraficaId: daAggiungere.anagraficaId,
          }}
          onChiudi={() => setDaAggiungere(null)}
          onSalvata={() => {
            setDaAggiungere(null);
            dati.ricarica();
            onCreate();
          }}
        />
      )}
    </>
  );
}

function formattaCrescita(p: number): string {
  const n = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 3 }).format(Math.abs(p));
  return `${p > 0 ? '+' : p < 0 ? '\u2212' : ''}${n} %`;
}



/**
 * Le voci ricorrenti: una riga con un nome, non un numero posato su una
 * casella. «Affitto ufficio, da gennaio, 1.200 € al mese» si legge, si discute
 * e si corregge; 1.200 scritto nella cella di marzo no — fra sei mesi nessuno
 * sa più cosa fosse.
 *
 * L'importo ha una **storia**: aumentare l'affitto da settembre non riscrive i
 * mesi già passati, che restano quello che erano davvero.
 */
function Ricorrenti({ categorie, onCambiate }: { categorie: Categoria[]; onCambiate: () => void }) {
  const avvisa = useAvvisi();
  const { organizzazione } = useSessione();
  const [nuova, setNuova] = useState(false);
  const [variazione, setVariazione] = useState<Ricorrente | null>(null);
  const [daModificare, setDaModificare] = useState<Ricorrente | null>(null);
  const ricorrenti = useCarico(() => api.get<Ricorrente[]>('/api/ricorrenti'), []);

  /**
   * Filtri e selezione. Con 147 voci — 129 clienti aggiunti in un colpo dai
   * suggerimenti — la lista va ristretta prima di poterla correggere, e
   * correggerla una riga alla volta non si fa.
   */
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroVerso, setFiltroVerso] = useState<'' | 'ENTRATA' | 'USCITA'>('');
  const [filtroTipo, setFiltroTipo] = useState<'' | Natura>('');
  const [cerca, setCerca] = useState('');
  const [selezionate, setSelezionate] = useState<Set<string>>(new Set());
  const [massiva, setMassiva] = useState(false);
  const { piatto } = useAlberoCategorie(categorie);

  const elimina = async (r: Ricorrente) => {
    if (!confirm(`Eliminare «${r.nome}»? Sparisce dal previsionale, passato compreso.`)) return;
    try {
      await api.del(`/api/ricorrenti/${r.id}`);
      avvisa('Voce ricorrente eliminata.');
      ricorrenti.ricarica();
      onCambiate();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  const elenco = ricorrenti.dati ?? [];

  // Una categoria comprende i suoi rami: filtrare «Acquisti» deve mostrare
  // anche le voci su «Acquisti › ISP».
  const ramoFiltrato = useMemo(() => {
    if (!filtroCategoria) return null;
    const ramo = new Set([filtroCategoria]);
    let cresciuto = true;
    while (cresciuto) {
      cresciuto = false;
      for (const c of categorie) {
        if (c.padreId && ramo.has(c.padreId) && !ramo.has(c.id)) {
          ramo.add(c.id);
          cresciuto = true;
        }
      }
    }
    return ramo;
  }, [filtroCategoria, categorie]);

  const visibili = elenco.filter(
    (r) =>
      (!ramoFiltrato || ramoFiltrato.has(r.categoriaId)) &&
      (!filtroVerso || r.verso === filtroVerso) &&
      (!filtroTipo || r.natura === filtroTipo) &&
      (!cerca.trim() || r.nome.toLowerCase().includes(cerca.trim().toLowerCase())),
  );
  // Le azioni valgono per le scelte **che si vedono**: una voce scelta e poi
  // nascosta da un filtro non deve cambiare senza che nessuno la veda.
  const scelte = visibili.filter((r) => selezionate.has(r.id));
  const alterna = (id: string) => {
    const n = new Set(selezionate);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelezionate(n);
  };

  const eliminaScelte = async () => {
    const alMese = scelte.reduce((t, r) => t + importoInVigore(r) / r.passoMesi, 0);
    if (
      !confirm(
        `Eliminare ${scelte.length} ${scelte.length === 1 ? 'voce' : 'voci'} (circa ${formattaEuro(Math.round(alMese))} al mese)?\n\n` +
          'Spariscono dal previsionale, passato compreso. Se vuoi solo fermarle da un certo mese, usa «Modifica in blocco» → «Fino a».',
      )
    )
      return;
    try {
      const esito = await api.del<{ eliminate: number }>('/api/ricorrenti', { ids: scelte.map((r) => r.id) });
      avvisa(`${esito.eliminate} ${esito.eliminate === 1 ? 'voce eliminata' : 'voci eliminate'}.`);
      setSelezionate(new Set());
      ricorrenti.ricarica();
      onCambiate();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  const cambiaNatura = async (r: Ricorrente) => {
    const natura: Natura = r.natura === 'STIMA' ? 'SCADENZA' : 'STIMA';
    try {
      await api.put(`/api/ricorrenti/${r.id}`, { natura });
      avvisa(`«${r.nome}» ora è: ${NATURE[natura].nome.toLowerCase()}.`);
      ricorrenti.ricarica();
      onCambiate();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Modifica non riuscita.', 'errore');
    }
  };

  return (
    <>
      <Scheda
        titolo="Voci ricorrenti"
        sottotitolo="Affitto, stipendi, canoni: una riga con un nome, un importo e un periodo. Alimentano il previsionale mese per mese, senza doverle riscrivere in ogni casella."
        senzaPadding
        azioni={
          <button className="bottone-secondario" onClick={() => setNuova(true)}>
            Nuova voce
          </button>
        }
      >
        {ricorrenti.caricamento ? (
          <Caricamento />
        ) : elenco.length === 0 ? (
          <Vuoto
            titolo="Nessuna voce ricorrente"
            testo="Sono le uscite e le entrate che si ripetono da sole: l’affitto, gli stipendi, i canoni. Una volta scritte, il previsionale le porta avanti da solo."
          />
        ) : (
          <>
          <div className="flex flex-wrap items-end gap-3 border-b border-inchiostro-200 px-4 py-3 text-sm">
            <label className="block">
              <span className="etichetta">Categoria</span>
              <select className="campo" value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
                <option value="">Tutte</option>
                {piatto
                  .filter((p) => !p.categoria.sistema)
                  .map(({ categoria, livello }) => (
                    <option key={categoria.id} value={categoria.id}>
                      {' '.repeat(livello * 2)}
                      {categoria.nome}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block">
              <span className="etichetta">Verso</span>
              <select className="campo" value={filtroVerso} onChange={(e) => setFiltroVerso(e.target.value as typeof filtroVerso)}>
                <option value="">Entrate e uscite</option>
                <option value="ENTRATA">Entrate</option>
                <option value="USCITA">Uscite</option>
              </select>
            </label>
            <label className="block">
              <span className="etichetta">Tipo</span>
              <select className="campo" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value as typeof filtroTipo)}>
                <option value="">Tutti</option>
                {(Object.keys(NATURE) as Natura[]).map((n) => (
                  <option key={n} value={n}>
                    {NATURE[n].nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="etichetta">Cerca</span>
              <input className="campo" placeholder="Nome della voce" value={cerca} onChange={(e) => setCerca(e.target.value)} />
            </label>
            <span className="pb-2 text-inchiostro-500">
              {visibili.length} di {elenco.length} ·{' '}
              <span className="num">
                {formattaEuro(Math.round(visibili.reduce((t, r) => t + (r.verso === 'ENTRATA' ? 1 : -1) * importoInVigore(r) / r.passoMesi, 0)))}
              </span>{' '}
              al mese, netto
            </span>
          </div>

          {scelte.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-inchiostro-200 bg-saldo-chiaro px-4 py-2 text-sm">
              <span className="font-medium">
                {scelte.length} {scelte.length === 1 ? 'voce scelta' : 'voci scelte'}
              </span>
              <button className="bottone-primario px-3 py-1 text-xs" onClick={() => setMassiva(true)}>
                Modifica in blocco…
              </button>
              <button className="bottone-secondario px-3 py-1 text-xs" onClick={() => void eliminaScelte()}>
                Elimina
              </button>
              <button
                className="text-xs text-inchiostro-500 underline decoration-dotted underline-offset-2"
                onClick={() => setSelezionate(new Set())}
              >
                Deseleziona
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th className="whitespace-nowrap">
                    <SelezionaTutti
                      scelte={scelte.length}
                      totale={visibili.length}
                      onCambia={(tutte) => {
                        const n = new Set(selezionate);
                        for (const r of visibili) {
                          if (tutte) n.add(r.id);
                          else n.delete(r.id);
                        }
                        setSelezionate(n);
                      }}
                    />
                  </th>
                  <th>Voce</th>
                  <th>Categoria</th>
                  <th title="Scadenza fissa: pesa anche sul mese in corso. Stima: pesa dai mesi futuri.">Tipo</th>
                  <th>Ogni</th>
                  <th
                    className="text-right"
                    title="Il giorno del mese in cui il movimento esce o entra. Decide il punto più basso della cassa nel previsionale: premi per cambiarlo."
                  >
                    Giorno
                  </th>
                  <th>Dal</th>
                  <th>Fino a</th>
                  <th className="text-right">Importo</th>
                  <th className="text-right">Crescita</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibili.map((r) => (
                  <tr key={r.id} className={r.attiva ? '' : 'text-inchiostro-400'}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selezionate.has(r.id)}
                        onChange={() => alterna(r.id)}
                        aria-label={`Scegli ${r.nome}`}
                      />
                    </td>
                    <td className="font-medium">
                      {r.nome}
                      {r.versioni.length > 1 && (
                        <span
                          className="ml-2 text-[10px] text-inchiostro-500"
                          title="L’importo è cambiato nel tempo: i mesi passati mantengono il valore che avevano."
                        >
                          {r.versioni.length} importi nel tempo
                        </span>
                      )}
                    </td>
                    <td>{r.categoria.nome}</td>
                    <td className="whitespace-nowrap">
                      {/* Si cambia con un clic sull'etichetta: è una scelta che
                          si corregge guardando il previsionale, non una cosa
                          per cui riaprire una finestra. */}
                      <button
                        className={`inline-block rounded border px-1.5 py-0.5 text-xs font-medium hover:ring-1 hover:ring-inchiostro-300 ${
                          r.natura === 'STIMA'
                            ? 'border-inchiostro-200 bg-inchiostro-100 text-inchiostro-700'
                            : 'border-saldo/20 bg-saldo-chiaro text-saldo'
                        }`}
                        title={`${NATURE[r.natura].spiegazione} Premi per cambiare.`}
                        onClick={() => void cambiaNatura(r)}
                      >
                        {NATURE[r.natura].nome}
                      </button>
                    </td>
                    <td className="whitespace-nowrap">{PASSI.find((p) => p.mesi === r.passoMesi)?.nome ?? `ogni ${r.passoMesi} mesi`}</td>
                    <td className="text-right">
                      <GiornoModificabile
                        giorno={r.giornoDelMese}
                        onSalva={async (giornoDelMese) => {
                          try {
                            await api.put(`/api/ricorrenti/${r.id}`, { giornoDelMese });
                            avvisa(`«${r.nome}» ora cade il ${giornoDelMese} del mese.`);
                            ricorrenti.ricarica();
                            onCambiate();
                          } catch (e) {
                            avvisa(e instanceof Error ? e.message : 'Modifica non riuscita.', 'errore');
                          }
                        }}
                      />
                    </td>
                    <td className="num whitespace-nowrap">{r.primoMese}</td>
                    <td className="num whitespace-nowrap">{r.ultimoMese ?? <span className="text-inchiostro-400">senza fine</span>}</td>
                    <td className="text-right">
                      <Importo cent={importoInVigore(r)} tono={r.verso === 'ENTRATA' ? 'incassi' : 'uscite'} />
                    </td>
                    {/* La crescita accanto all'importo: senza, la riga dice
                        22.000 € e il prospetto ne mostra 23.339,80 tre mesi
                        dopo, senza che si capisca da dove venga. */}
                    <td className="num text-right">
                      {crescitaInVigore(r) === 0 ? (
                        <span className="text-inchiostro-400">—</span>
                      ) : (
                        <span
                          className={crescitaInVigore(r) > 0 ? 'text-incassi' : 'text-uscite'}
                          title={`A ogni ricorrenza. Fra sei: ${formattaEuro(
                            serieCrescita(importoInVigore(r), crescitaInVigore(r), 6)[5],
                          )}`}
                        >
                          {formattaCrescita(crescitaInVigore(r))}
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          className="bottone-secondario px-2 py-1 text-xs"
                          title="Corregge la voce: categoria, nome, giorno, ricorrenza, fine, tipo, o l’importo se è sbagliato."
                          onClick={() => setDaModificare(r)}
                        >
                          Modifica
                        </button>
                        <button
                          className="bottone-secondario px-2 py-1 text-xs"
                          title="L’importo cambia da una certa data in avanti, e i mesi già passati restano com’erano."
                          onClick={() => setVariazione(r)}
                        >
                          Variazione
                        </button>
                        <button className="bottone-secondario px-2 py-1 text-xs" onClick={() => elimina(r)}>
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Scheda>

      {massiva && (
        <FormMassiva
          voci={scelte}
          categorie={categorie}
          onChiudi={() => setMassiva(false)}
          onSalvata={() => {
            setMassiva(false);
            setSelezionate(new Set());
            ricorrenti.ricarica();
            onCambiate();
          }}
        />
      )}

      <ProposteRicorrenti
        categorie={categorie}
        onCreate={() => {
          ricorrenti.ricarica();
          onCambiate();
        }}
      />

      {nuova && (
        <FormRicorrente
          categorie={categorie}
          formatoData={organizzazione.formatoData}
          onChiudi={() => setNuova(false)}
          onSalvata={() => {
            setNuova(false);
            ricorrenti.ricarica();
            onCambiate();
          }}
        />
      )}
      {daModificare && (
        <FormModifica
          ricorrente={daModificare}
          categorie={categorie}
          onChiudi={() => setDaModificare(null)}
          onSalvata={() => {
            setDaModificare(null);
            ricorrenti.ricarica();
            onCambiate();
          }}
        />
      )}
      {variazione && (
        <FormVariazione
          ricorrente={variazione}
          onChiudi={() => setVariazione(null)}
          onSalvata={() => {
            setVariazione(null);
            ricorrenti.ricarica();
            onCambiate();
          }}
        />
      )}
    </>
  );
}


/**
 * Il campo della crescita, con **quello che produce** subito sotto.
 *
 * Una percentuale è astratta: «+3 %» non dice a nessuno dove si arriva fra sei
 * mesi, e sceglierla al buio significa scriverne una e scoprire l'effetto nel
 * prospetto. I numeri invece si giudicano a colpo d'occhio — «22.000 → 22.660
 * → 23.339,80» si riconosce come giusto o come troppo.
 *
 * La serie è calcolata da `@mastro/core`, la stessa funzione che il
 * previsionale usa per davvero: un'anteprima che diverge anche di un centesimo
 * fa perdere fiducia a tutti e due i numeri.
 */
function CampoCrescita({
  crescita,
  onCambia,
  importoCent,
  passoMesi,
  verso,
}: {
  crescita: number;
  onCambia: (v: number) => void;
  importoCent: number;
  passoMesi: number;
  verso: 'ENTRATA' | 'USCITA';
}) {
  const ogni = PASSI.find((p) => p.mesi === passoMesi)?.nome ?? `ogni ${passoMesi} mesi`;
  const serie = serieCrescita(importoCent, crescita, 6);
  /**
   * Il testo che si sta scrivendo, tenuto a parte dal numero.
   *
   * Prima il campo si ridisegnava dal numero a ogni tasto: «1,» diventava 1 e
   * la virgola spariva, «-» diventava 0. Decimali e cali, in pratica, non si
   * potevano scrivere. Si accettano fino a tre decimali, quanti ne tiene il
   * database, con la virgola o col punto.
   */
  const [testo, setTesto] = useState(crescita === 0 ? '' : String(crescita).replace('.', ','));
  return (
    <>
      <Campo
        etichetta={`Crescita attesa, a ogni ricorrenza (${ogni})`}
        aiuto="Si applica a ogni ricorrenza e si compone. Lascia a zero per un importo che resta fermo; usa un valore negativo per qualcosa che cala."
      >
        <div className="flex items-center gap-2">
          <div className="w-28">
            <input
              className="campo num text-right"
              aria-label="Crescita percentuale"
              inputMode="decimal"
              value={testo}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (!/^-?\d{0,3}([.,]\d{0,3})?$/.test(v)) return;
                setTesto(v);
                const n = Number(v.replace(',', '.'));
                onCambia(v === '' || v === '-' || !Number.isFinite(n) ? 0 : n);
              }}
              placeholder="0,000"
            />
          </div>
          <span className="text-sm text-inchiostro-500">% {ogni} — anche con decimali (0,5) o negativa (−1,25)</span>
        </div>
      </Campo>

      {crescita !== 0 && importoCent > 0 && (
        <div className="rounded-md bg-inchiostro-50 p-3 text-sm">
          <p className="mb-1.5 text-xs font-medium text-inchiostro-600">
            Le prime sei ricorrenze, con questa crescita
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {serie.map((c, i) => (
              <span key={i} className="whitespace-nowrap">
                <span className="text-xs text-inchiostro-500">{i + 1}ª</span>{' '}
                <Importo cent={c} tono={verso === 'ENTRATA' ? 'incassi' : 'uscite'} />
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-inchiostro-500">
            Sulla sesta fa {formattaCrescita(((serie[5] - importoCent) / importoCent) * 100)} rispetto alla prima.
            {crescita > 20 && ' Attenzione: componendo, una crescita alta esplode in fretta.'}
          </p>
        </div>
      )}
    </>
  );
}

/** Nuova voce ricorrente. Il periodo è «da un mese, per una durata o senza fine». */
/**
 * Corregge più voci insieme.
 *
 * Ogni campo ha la sua spunta «cambia»: si tocca solo quello che si vuole, e il
 * resto di ogni voce resta com'era. La categoria si può cambiare solo se le
 * voci scelte sono tutte entrate o tutte uscite — una categoria sola non può
 * andar bene per le due cose, e lo controlla anche il server. L'importo qui non
 * c'è: su voci diverse «lo stesso importo» non ha senso, e cambiarlo da una data
 * è una variazione, voce per voce.
 */
function FormMassiva({
  voci,
  categorie,
  onChiudi,
  onSalvata,
}: {
  voci: Ricorrente[];
  categorie: Categoria[];
  onChiudi: () => void;
  onSalvata: () => void;
}) {
  const avvisa = useAvvisi();
  const { piatto } = useAlberoCategorie(categorie);
  const versi = new Set(voci.map((v) => v.verso));
  const versoUnico = versi.size === 1 ? voci[0].verso : null;
  const assegnabili = piatto.filter((p) => p.categoria.verso === versoUnico && !p.categoria.sistema);

  const [cambia, setCambia] = useState({ categoria: false, natura: false, giorno: false, fine: false, attiva: false });
  const [categoriaId, setCategoriaId] = useState('');
  const [natura, setNatura] = useState<Natura>('STIMA');
  const [giorno, setGiorno] = useState(1);
  const [senzaFine, setSenzaFine] = useState(false);
  const [ultimoMese, setUltimoMese] = useState('');
  const [attiva, setAttiva] = useState(true);
  const [attesa, setAttesa] = useState(false);

  const campi: Record<string, unknown> = {};
  if (cambia.categoria && categoriaId) campi.categoriaId = categoriaId;
  if (cambia.natura) campi.natura = natura;
  if (cambia.giorno) campi.giornoDelMese = giorno;
  if (cambia.fine) campi.ultimoMese = senzaFine ? null : ultimoMese || undefined;
  if (cambia.attiva) campi.attiva = attiva;
  const pronto = Object.values(campi).some((v) => v !== undefined) && !(cambia.categoria && !categoriaId);

  const salva = async () => {
    setAttesa(true);
    try {
      const esito = await api.put<{ aggiornate: number }>('/api/ricorrenti', { ids: voci.map((v) => v.id), campi });
      avvisa(`${esito.aggiornate} ${esito.aggiornate === 1 ? 'voce corretta' : 'voci corrette'}.`);
      onSalvata();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Modifica non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  const riga = (chiave: keyof typeof cambia, etichetta: string, controllo: ReactNode, nota?: string) => (
    <div className="grid grid-cols-[9rem_1fr] items-start gap-3">
      <label className="flex items-center gap-2 pt-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={cambia[chiave]}
          onChange={(e) => setCambia({ ...cambia, [chiave]: e.target.checked })}
        />
        {etichetta}
      </label>
      <div className={cambia[chiave] ? '' : 'pointer-events-none opacity-40'}>
        {controllo}
        {nota && <p className="mt-1 text-xs text-inchiostro-500">{nota}</p>}
      </div>
    </div>
  );

  return (
    <Dialogo
      aperto
      titolo={`Modifica in blocco · ${voci.length} ${voci.length === 1 ? 'voce' : 'voci'}`}
      onChiudi={onChiudi}
      larghezza="max-w-2xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-inchiostro-600">
          Spunta quello che vuoi cambiare: il resto di ogni voce resta com’era. Per l’importo usa «Variazione», voce per
          voce.
        </p>
        {riga(
          'categoria',
          'Categoria',
          versoUnico ? (
            <select className="campo" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
              <option value="">Scegli…</option>
              {assegnabili.map(({ categoria, livello }) => (
                <option key={categoria.id} value={categoria.id}>
                  {' '.repeat(livello * 2)}
                  {categoria.nome}
                </option>
              ))}
            </select>
          ) : (
            <p className="pt-2 text-sm text-attenzione">
              Hai scelto entrate e uscite insieme: una categoria sola non va bene per tutte. Filtra per verso.
            </p>
          ),
        )}
        {riga(
          'natura',
          'Tipo',
          <select className="campo" value={natura} onChange={(e) => setNatura(e.target.value as Natura)}>
            {(Object.keys(NATURE) as Natura[]).map((n) => (
              <option key={n} value={n}>
                {NATURE[n].nome}
              </option>
            ))}
          </select>,
          NATURE[natura].spiegazione,
        )}
        {riga(
          'giorno',
          'Giorno del mese',
          <input
            type="number"
            min={1}
            max={31}
            className="campo num w-24 text-right"
            value={giorno}
            onChange={(e) => setGiorno(Math.min(31, Math.max(1, Number(e.target.value) || 1)))}
          />,
          'Il giorno in cui escono o entrano davvero: decide il punto più basso della cassa.',
        )}
        {riga(
          'fine',
          'Fino a',
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="month"
              className="campo"
              value={ultimoMese}
              disabled={senzaFine}
              onChange={(e) => setUltimoMese(e.target.value)}
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={senzaFine} onChange={(e) => setSenzaFine(e.target.checked)} />
              senza fine
            </label>
          </div>,
          'Per fermare delle voci da un certo mese senza cancellarne il passato.',
        )}
        {riga(
          'attiva',
          'Stato',
          <select className="campo" value={attiva ? 'si' : 'no'} onChange={(e) => setAttiva(e.target.value === 'si')}>
            <option value="si">Attive: pesano sul previsionale</option>
            <option value="no">Ferme: restano in elenco ma non pesano</option>
          </select>,
        )}
        <div className="flex justify-end gap-2 border-t border-inchiostro-200 pt-3">
          <button className="bottone-secondario" onClick={onChiudi} disabled={attesa}>
            Annulla
          </button>
          <button className="bottone-primario" onClick={() => void salva()} disabled={attesa || !pronto}>
            {attesa ? 'Salvo…' : `Applica a ${voci.length} ${voci.length === 1 ? 'voce' : 'voci'}`}
          </button>
        </div>
      </div>
    </Dialogo>
  );
}

/**
 * Corregge una voce già scritta.
 *
 * È diversa dalla variazione, e la differenza conta: la **variazione** dice che
 * da una certa data l'importo cambia davvero, e i mesi prima restano com'erano;
 * la **modifica** dice che la voce era scritta male — una categoria sbagliata,
 * un giorno sbagliato, un refuso nell'importo — e la corregge per tutti i mesi,
 * senza creare storia. Prima la modifica non c'era, e una categoria scelta male
 * restava lì.
 */
function FormModifica({
  ricorrente,
  categorie,
  onChiudi,
  onSalvata,
}: {
  ricorrente: Ricorrente;
  categorie: Categoria[];
  onChiudi: () => void;
  onSalvata: () => void;
}) {
  const avvisa = useAvvisi();
  const { piatto } = useAlberoCategorie(categorie);
  const [nome, setNome] = useState(ricorrente.nome);
  const [categoriaId, setCategoriaId] = useState(ricorrente.categoriaId);
  const [giornoDelMese, setGiornoDelMese] = useState(ricorrente.giornoDelMese);
  const [passoMesi, setPassoMesi] = useState(ricorrente.passoMesi);
  const [ultimoMese, setUltimoMese] = useState<string | null>(ricorrente.ultimoMese);
  const [natura, setNatura] = useState<Natura>(ricorrente.natura);
  const inVigore = importoInVigore(ricorrente);
  const [importoCent, setImportoCent] = useState(inVigore);
  const [attesa, setAttesa] = useState(false);

  // Solo le categorie dello stesso verso: una voce di uscita sotto una
  // categoria di entrata cambierebbe segno nel previsionale.
  const assegnabili = piatto.filter((p) => p.categoria.verso === ricorrente.verso && !p.categoria.sistema);
  // La categoria sbagliata può essere proprio di un altro verso: allora nel
  // menu non c'è, e il menu non deve fingere di mostrarla con la prima voce.
  const categoriaValida = assegnabili.some((p) => p.categoria.id === categoriaId);

  const salva = async () => {
    if (!nome.trim()) return avvisa('Dai un nome alla voce.', 'errore');
    if (!categoriaValida) return avvisa('Scegli una categoria.', 'errore');
    setAttesa(true);
    try {
      await api.put(`/api/ricorrenti/${ricorrente.id}`, {
        nome: nome.trim(),
        categoriaId,
        giornoDelMese,
        passoMesi,
        ultimoMese,
        natura,
        ...(importoCent !== inVigore ? { importoInVigoreCent: importoCent } : {}),
      });
      avvisa(`«${nome.trim()}» corretta.`);
      onSalvata();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Modifica non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo={`Modifica · ${ricorrente.nome}`} onChiudi={onChiudi} larghezza="max-w-2xl">
      <div className="space-y-4">
        <Campo etichetta="Descrizione">
          <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
        </Campo>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta={`Categoria (${ricorrente.verso === 'USCITA' ? 'uscite' : 'entrate'})`}>
            <select
              className={`campo ${categoriaValida ? '' : 'border-allerta'}`}
              value={categoriaValida ? categoriaId : ''}
              onChange={(e) => setCategoriaId(e.target.value)}
            >
              {!categoriaValida && <option value="">Scegli…</option>}
              {assegnabili.map(({ categoria, livello }) => (
                <option key={categoria.id} value={categoria.id}>
                  {' '.repeat(livello * 2)}
                  {categoria.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Tipo">
            <select className="campo" value={natura} onChange={(e) => setNatura(e.target.value as Natura)}>
              {(Object.keys(NATURE) as Natura[]).map((n) => (
                <option key={n} value={n}>
                  {NATURE[n].nome}
                </option>
              ))}
            </select>
          </Campo>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo etichetta="Ricorrenza">
            <select className="campo" value={passoMesi} onChange={(e) => setPassoMesi(Number(e.target.value))}>
              {PASSI.map((p) => (
                <option key={p.mesi} value={p.mesi}>
                  {p.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Giorno del mese">
            <input
              type="number"
              min={1}
              max={31}
              className="campo num text-right"
              value={giornoDelMese}
              onChange={(e) => setGiornoDelMese(Math.min(31, Math.max(1, Number(e.target.value) || 1)))}
            />
          </Campo>
          <Campo etichetta="Fino a" aiuto="Vuoto: senza fine.">
            <input
              type="month"
              className="campo"
              value={ultimoMese ?? ''}
              min={ricorrente.primoMese}
              onChange={(e) => setUltimoMese(e.target.value || null)}
            />
          </Campo>
        </div>
        <Campo
          etichetta="Importo in vigore"
          aiuto="Corregge l’importo attuale senza creare storia: è per i refusi. Se l’importo cambia davvero da una certa data, usa «Variazione», così i mesi prima restano com’erano."
        >
          <CampoImporto valoreCent={importoCent} onCambia={setImportoCent} />
        </Campo>
        <div className="flex justify-end gap-2">
          <button className="bottone-secondario" onClick={onChiudi} disabled={attesa}>
            Annulla
          </button>
          <button className="bottone-primario" onClick={() => void salva()} disabled={attesa || !categoriaValida}>
            {attesa ? 'Salvo…' : 'Salva'}
          </button>
        </div>
      </div>
    </Dialogo>
  );
}

/** Valori con cui aprire il modulo: una proposta dallo storico, da correggere prima di salvare. */
interface VoceIniziale {
  nome: string;
  verso: 'ENTRATA' | 'USCITA';
  categoriaId: string | null;
  importoCent: number;
  passoMesi: number;
  primoMese: string;
  giornoDelMese: number;
  natura: Natura;
  anagraficaId: string;
}

function FormRicorrente({
  categorie,
  iniziale,
  onChiudi,
  onSalvata,
}: {
  categorie: Categoria[];
  formatoData: 'gg/mm/aaaa' | 'gg-mm-aaaa' | 'aaaa-mm-gg';
  /**
   * Quando c'è, il modulo parte compilato con una proposta: frequenza,
   * importo, mese di partenza e durata si correggono qui prima di salvare,
   * invece di accettare la proposta così com'è.
   */
  iniziale?: VoceIniziale;
  onChiudi: () => void;
  onSalvata: () => void;
}) {
  const avvisa = useAvvisi();
  const { piatto } = useAlberoCategorie(categorie);
  const [nome, setNome] = useState(iniziale?.nome ?? '');
  const [verso, setVerso] = useState<'ENTRATA' | 'USCITA'>(iniziale?.verso ?? 'USCITA');
  const [categoriaId, setCategoriaId] = useState(iniziale?.categoriaId ?? '');
  const [importoCent, setImportoCent] = useState(iniziale?.importoCent ?? 0);
  const [crescita, setCrescita] = useState(0);
  const [primoMese, setPrimoMese] = useState(iniziale?.primoMese ?? meseDi(oggi()));
  const [senzaFine, setSenzaFine] = useState(true);
  const [durata, setDurata] = useState(12);
  const [passoMesi, setPassoMesi] = useState(iniziale?.passoMesi ?? 1);
  const [giornoDelMese, setGiornoDelMese] = useState(iniziale?.giornoDelMese ?? 1);
  const [natura, setNatura] = useState<Natura>(iniziale?.natura ?? 'SCADENZA');
  const [attesa, setAttesa] = useState(false);

  const assegnabili = piatto.filter((p) => p.categoria.verso === verso && !p.categoria.sistema);
  const ultimoMese = senzaFine ? null : mesiDa(primoMese, durata).at(-1) ?? null;

  const salva = async () => {
    setAttesa(true);
    try {
      await api.post('/api/ricorrenti', {
        nome: nome.trim(),
        categoriaId,
        verso,
        passoMesi,
        primoMese,
        ultimoMese,
        giornoDelMese,
        natura,
        importoCent,
        crescitaPercentuale: crescita,
        // Legata alla controparte da cui viene la proposta: la fattura vera
        // prenderà il posto della stima del suo mese.
        anagraficaId: iniziale?.anagraficaId ?? null,
      });
      avvisa('Voce ricorrente creata: da ora alimenta il previsionale.');
      onSalvata();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Creazione non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo
      aperto
      titolo={iniziale ? `Aggiungi al budget · ${iniziale.nome}` : "Nuova voce ricorrente"}
      onChiudi={onChiudi}
      larghezza="max-w-2xl"
    >
      <div className="space-y-4">
        <Campo etichetta="Descrizione" aiuto="Come la chiami tu: «Affitto ufficio», «Stipendio Rossi», «Canone gestionale».">
          <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Affitto ufficio" autoFocus />
        </Campo>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta="Entrata o uscita">
            <select
              className="campo"
              value={verso}
              onChange={(e) => {
                setVerso(e.target.value as 'ENTRATA' | 'USCITA');
                setCategoriaId('');
              }}
            >
              <option value="USCITA">Uscita</option>
              <option value="ENTRATA">Entrata</option>
            </select>
          </Campo>
          <Campo etichetta="Voce del previsionale">
            <select className="campo" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
              <option value="">Scegli…</option>
              {assegnabili.map(({ categoria, livello }) => (
                <option key={categoria.id} value={categoria.id}>
                  {' '.repeat(livello * 2)}
                  {categoria.nome}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Campo etichetta="Importo">
            <CampoImporto valoreCent={importoCent} onCambia={setImportoCent} />
          </Campo>
          <Campo etichetta="Ricorrenza">
            <select className="campo" value={passoMesi} onChange={(e) => setPassoMesi(Number(e.target.value))}>
              {PASSI.map((p) => (
                <option key={p.mesi} value={p.mesi}>
                  {p.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Giorno del mese" aiuto="Il giorno in cui esce o entra davvero: decide dove cade il punto più basso della cassa. Nei mesi più corti vale l’ultimo giorno.">
            <input
              type="number"
              min={1}
              max={31}
              className="campo num text-right"
              value={giornoDelMese}
              onChange={(e) => setGiornoDelMese(Number(e.target.value))}
            />
          </Campo>
        </div>

        <fieldset className="space-y-1.5">
          <legend className="etichetta">Che cos’è</legend>
          {(Object.keys(NATURE) as Natura[]).map((n) => (
            <label key={n} className="flex items-start gap-2 text-sm">
              <input type="radio" className="mt-1" checked={natura === n} onChange={() => setNatura(n)} />
              <span>
                <strong>{NATURE[n].nome}</strong>
                <span className="block text-xs text-inchiostro-500">{NATURE[n].spiegazione}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <CampoCrescita
          crescita={crescita}
          onCambia={setCrescita}
          importoCent={importoCent}
          passoMesi={passoMesi}
          verso={verso}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etichetta="Dal mese">
            <input type="month" className="campo" value={primoMese} onChange={(e) => setPrimoMese(e.target.value)} />
          </Campo>
          <Campo etichetta="Durata">
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={senzaFine} onChange={() => setSenzaFine(true)} />
                Senza fine
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={!senzaFine} onChange={() => setSenzaFine(false)} />
                Per
                <input
                  type="number"
                  min={1}
                  max={120}
                  className="campo num w-20 py-1 text-right"
                  value={durata}
                  onChange={(e) => {
                    setDurata(Number(e.target.value));
                    setSenzaFine(false);
                  }}
                />
                mesi
              </label>
            </div>
          </Campo>
        </div>

        <p className="rounded-md bg-inchiostro-100 p-3 text-sm">
          {nome.trim() ? <strong>{nome.trim()}</strong> : 'La voce'} peserà{' '}
          <span className="num font-medium">{formattaEuro(importoCent)}</span>{' '}
          {PASSI.find((p) => p.mesi === passoMesi)?.nome} dal <span className="num">{primoMese}</span>{' '}
          {ultimoMese ? (
            <>
              fino a <span className="num">{ultimoMese}</span>
            </>
          ) : (
            'e da lì in avanti finché non la togli'
          )}
          {/* Con la crescita la frase «peserà X ogni mese» sarebbe falsa: X è
              solo la prima volta. Dirlo qui evita di doverlo scoprire dal
              prospetto. */}
          {crescita !== 0 && (
            <>
              , crescendo del <span className="num">{formattaCrescita(crescita)}</span> a ogni ricorrenza — la sesta
              vale <span className="num font-medium">{formattaEuro(serieCrescita(importoCent, crescita, 6)[5])}</span>
            </>
          )}
          .
        </p>
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-inchiostro-200 pt-4">
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={salva} disabled={attesa || !nome.trim() || !categoriaId || importoCent === 0}>
          Crea la voce
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Variazione datata: l'importo cambia **da** una certa data, e i mesi già
 * passati restano quello che erano. È la differenza fra correggere un refuso e
 * registrare che l'affitto è aumentato.
 */
function FormVariazione({
  ricorrente,
  onChiudi,
  onSalvata,
}: {
  ricorrente: Ricorrente;
  onChiudi: () => void;
  onSalvata: () => void;
}) {
  const avvisa = useAvvisi();
  const [decorrenza, setDecorrenza] = useState(`${meseDi(oggi())}-01`);
  const [importoCent, setImportoCent] = useState(importoInVigore(ricorrente));
  const [crescita, setCrescita] = useState(crescitaInVigore(ricorrente));
  const [nota, setNota] = useState('');
  const [attesa, setAttesa] = useState(false);

  const salva = async () => {
    setAttesa(true);
    try {
      await api.post(`/api/ricorrenti/${ricorrente.id}/variazione`, {
        decorrenza,
        importoCent,
        crescitaPercentuale: crescita,
        nota: nota.trim() || undefined,
      });
      avvisa('Variazione registrata: i mesi precedenti restano com’erano.');
      onSalvata();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Variazione non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo={`Variazione di «${ricorrente.nome}»`} onChiudi={onChiudi}>
      <p className="mb-3 text-sm text-inchiostro-600">
        Adesso vale <span className="num font-medium">{formattaEuro(importoInVigore(ricorrente))}</span>. Il nuovo
        importo vale <strong>dalla data indicata in avanti</strong>: i mesi già passati non si toccano.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etichetta="A partire dal">
          <input type="date" className="campo" value={decorrenza} onChange={(e) => setDecorrenza(e.target.value)} />
        </Campo>
        <Campo etichetta="Nuovo importo">
          <CampoImporto valoreCent={importoCent} onCambia={setImportoCent} />
        </Campo>
      </div>
      {/* Anche la crescita si può cambiare: una variazione dichiara un nuovo
          punto di partenza, e da lì ricomincia a comporre. */}
      <div className="mt-3">
        <CampoCrescita
          crescita={crescita}
          onCambia={setCrescita}
          importoCent={importoCent}
          passoMesi={ricorrente.passoMesi}
          verso={ricorrente.verso}
        />
      </div>

      <Campo etichetta="Perché" aiuto="Fra un anno nessuno si ricorda perché è cambiato: scriverlo costa un attimo.">
        <input className="campo" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Es. adeguamento ISTAT" />
      </Campo>

      {ricorrente.versioni.length > 0 && (
        <div className="mt-3 rounded-md bg-inchiostro-100 p-3 text-xs">
          <p className="mb-1 font-medium">Storia degli importi</p>
          <ul className="space-y-0.5">
            {ricorrente.versioni.map((v) => (
              <li key={v.id}>
                <span className="num">{v.validita}</span> → <span className="num">{formattaEuro(v.importoCent)}</span>
                {Number(v.crescitaPercentuale) !== 0 && (
                  <span className="num text-inchiostro-500"> {formattaCrescita(Number(v.crescitaPercentuale))}</span>
                )}
                {v.nota && <span className="text-inchiostro-500"> · {v.nota}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2 border-t border-inchiostro-200 pt-4">
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={salva} disabled={attesa}>
          Registra la variazione
        </button>
      </div>
    </Dialogo>
  );
}
